/*
  IA PDP Gallery — <ia-pdp-gallery>. Main keen-slider + thumbnail rail
  (adapted from the theme slider conventions: .js-ia-slider-prev/next).

  - Thumbnails use perView "auto" because CSS owns their width (a
    1/--ia-pdp-thumbs-per-view share of the rail); keen only gets the gap.
  - .ia-pdp-gallery__overlay elements (badge, icon badge, accent) belong to
    the first image only: any other slide gets .is-not-first-slide on the host
    and the CSS fades them out.
  - Variant-assigned media are stacked in slide 0 (one visible at a time);
    unassigned media follow as their own slides. On variant:change the new
    data-featured-media-id is read from the fetched section HTML and only the
    active image in slide 0 swaps — no client-side media mapping, no re-layout.
  - window.iaTheme.ThumbnailPlugin (main ↔ thumbs sync) lives here — it was
    historically defined by the product gallery module.
*/
import KeenSlider from 'ia-keen-slider'
import { EVENTS, publish, subscribe } from 'ia-util-pubsub'

// keen reports no slidesPerView in "auto" mode — derive it from the slide pitch.
const visibleThumbCount = (details) => {
  const slides = details?.slides
  if (!slides?.length) return 1
  const step = slides.length > 1 ? slides[1].distance - slides[0].distance : slides[0].size
  return step > 0 ? Math.max(1, Math.round(1 / step)) : 1
}

window.iaTheme = window.iaTheme || {}
window.iaTheme.ThumbnailPlugin = (main) => {
  return (slider) => {
    const removeActive = () => {
      slider.slides.forEach((slide) => slide.classList.remove('active'))
    }
    const addActive = (idx) => {
      slider.slides[idx]?.classList.add('active')
    }
    const addClickEvents = () => {
      slider.slides.forEach((slide, idx) => {
        slide.addEventListener('click', () => main.moveToIdx(idx))
      })
    }

    slider.on('created', () => {
      addActive(slider.track.details.rel)
      addClickEvents()
      main.on('animationStarted', (mainSlider) => {
        removeActive()
        const next = mainSlider.animator.targetIdx || 0
        const relativeIndex = mainSlider.track.absToRel(next)
        addActive(relativeIndex)

        const thumbDetails = slider.track.details
        let targetIdx = relativeIndex - Math.floor(visibleThumbCount(thumbDetails) / 2)
        targetIdx = Math.max(0, Math.min(thumbDetails.maxIdx, targetIdx))
        slider.moveToIdx(targetIdx)
      })
    })
  }
}

if (!customElements.get('ia-pdp-gallery')) {
  class IaPdpGallery extends HTMLElement {
    connectedCallback() {
      this.mainSliderEl = this.querySelector('.ia-pdp-gallery__slider .keen-slider')
      this.thumbSliderEl = this.querySelector('.ia-pdp-gallery__thumbs .keen-slider')
      this.loop = this.dataset.loop === 'true'

      this.initSliders()
      this.unsubscribeVariantChange = subscribe(EVENTS.variantChange, this.handleVariantChange.bind(this))
    }

    disconnectedCallback() {
      this.unsubscribeVariantChange?.()
      this.mainSlider?.destroy()
      this.thumbSlider?.destroy()
    }

    get sectionId() {
      return this.getAttribute('section-id')
    }

    get productId() {
      return this.getAttribute('product-id')
    }

    initSliders() {
      if (!this.mainSliderEl) return

      this.mainSlider = new KeenSlider(this.mainSliderEl, {
        loop: this.loop,
        created: () => {
          this.classList.remove('is-loading')
          this.handleSlideChange(0)
        },
        slideChanged: (slider) => {
          const index = slider.track.details.rel
          this.handleSlideChange(index)
          // Analytics hook (view_product_image); position is 1-based like the
          // thumbnails' "Go to image N" labels.
          publish(EVENTS.pdpMediaChange, {
            detail: { productId: this.productId, sectionId: this.sectionId, index, position: index + 1 }
          })
        }
      })

      if (this.thumbSliderEl) {
        const gap = parseFloat(getComputedStyle(this.thumbSliderEl).getPropertyValue('--ia-pdp-thumbs-gap'))
        this.thumbSlider = new KeenSlider(
          this.thumbSliderEl,
          {
            initial: 0,
            slides: {
              perView: 'auto',
              spacing: Number.isFinite(gap) ? gap : 2
            }
          },
          [window.iaTheme.ThumbnailPlugin(this.mainSlider)]
        )
      }

      this.querySelectorAll('.js-ia-slider-prev').forEach((button) => {
        button.addEventListener('click', () => this.mainSlider.prev())
      })
      this.querySelectorAll('.js-ia-slider-next').forEach((button) => {
        button.addEventListener('click', () => this.mainSlider.next())
      })
    }

    handleSlideChange(activeIndex) {
      this.updateArrows(activeIndex)
      // Badge / icon badge / accent are part of the first image only
      this.classList.toggle('is-not-first-slide', activeIndex !== 0)
    }

    updateArrows(activeIndex) {
      if (this.loop || !this.mainSlider) return
      const lastIndex = this.mainSlider.slides.length - 1
      this.querySelectorAll('.js-ia-slider-prev').forEach((button) => {
        button.classList.toggle('ia-hidden', activeIndex === 0)
      })
      this.querySelectorAll('.js-ia-slider-next').forEach((button) => {
        button.classList.toggle('ia-hidden', activeIndex === lastIndex)
      })
    }

    /* The variant images live stacked in slide 0, so switching variant only
       swaps which one is shown — the slide count never changes and keen needs
       no re-layout. */
    showVariantMedia(mediaId) {
      const id = String(mediaId)
      const targets = [...this.querySelectorAll('[data-variant-media]')]
      // Unknown id: keep the current image rather than blanking the slide.
      if (!targets.some((el) => el.dataset.variantMedia === id)) return
      targets.forEach((el) => el.classList.toggle('is-active', el.dataset.variantMedia === id))
      this.mainSlider?.moveToIdx(0)
    }

    handleVariantChange(event) {
      const { html, sectionId, productId } = event.detail
      if (sectionId !== this.sectionId || productId !== this.productId || !html) return
      const mediaId = html.querySelector('ia-pdp-gallery')?.dataset.featuredMediaId
      if (!mediaId) return
      this.dataset.featuredMediaId = mediaId
      this.showVariantMedia(mediaId)
    }
  }

  customElements.define('ia-pdp-gallery', IaPdpGallery)
}
